import { useState, type FormEvent } from 'react';
import { Navigate, useNavigate } from 'react-router';
import {
  Box, Paper, Typography, TextField, Button, Alert, Stack, Divider, Chip,
} from '@mui/material';
import { useMutation, useQuery } from '@tanstack/react-query';
import { api } from '../lib/api';
import { useAuth } from '../hooks/useAuth';

interface DevUser {
  id: string;
  email: string;
  name: string;
  role: 'family' | 'nurse' | 'admin';
}

export default function Login() {
  const { user, isLoading, refresh } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [sent, setSent] = useState(false);

  const mutation = useMutation({
    mutationFn: (em: string) => api.post<{ ok: true }>('/api/auth/magic-link', { email: em }),
    onSuccess: () => setSent(true),
  });

  const devUsersQuery = useQuery({
    queryKey: ['auth', 'dev-users'],
    queryFn: () => api.get<DevUser[]>('/api/auth/dev-users'),
    enabled: import.meta.env.DEV,
    retry: false,
    staleTime: Infinity,
  });

  const devLoginMutation = useMutation({
    mutationFn: (em: string) => api.post<{ ok: true }>('/api/auth/dev-login', { email: em }),
    onSuccess: async () => {
      await refresh();
      navigate('/', { replace: true });
    },
  });

  if (isLoading) return null;
  if (user) return <Navigate to="/" replace />;

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    mutation.mutate(email.trim());
  };

  return (
    <Box sx={{
      position: 'fixed',
      inset: 0,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      bgcolor: 'background.default',
      px: 2,
      overflowY: 'auto',
    }}>
      <Paper sx={{ p: 4, maxWidth: 420, width: '100%', my: 4 }} elevation={0} variant="outlined">
        <Stack spacing={3}>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 600 }}>Care</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              Private family sign-in. We'll email you a one-time link.
            </Typography>
          </Box>
          {sent ? (
            <Alert severity="success">
              If your email is on the allow-list, a sign-in link is on its way. Check your inbox.
            </Alert>
          ) : (
            <form onSubmit={onSubmit}>
              <Stack spacing={2}>
                <TextField
                  label="Email"
                  type="email"
                  autoComplete="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  fullWidth
                  autoFocus
                />
                {mutation.isError && (
                  <Alert severity="error">{(mutation.error as Error).message}</Alert>
                )}
                <Button
                  type="submit"
                  variant="contained"
                  size="large"
                  disabled={mutation.isPending || !email}
                  fullWidth
                >
                  {mutation.isPending ? 'Sending…' : 'Send magic link'}
                </Button>
              </Stack>
            </form>
          )}

          {import.meta.env.DEV && (devUsersQuery.data ?? []).length > 0 && (
            <>
              <Divider>
                <Chip label="Dev quick login" size="small" />
              </Divider>
              {devLoginMutation.isError && (
                <Alert severity="error">{(devLoginMutation.error as Error).message}</Alert>
              )}
              <Stack spacing={1}>
                {(devUsersQuery.data ?? []).map((u) => (
                  <Button
                    key={u.id}
                    variant="outlined"
                    onClick={() => devLoginMutation.mutate(u.email)}
                    disabled={devLoginMutation.isPending}
                    fullWidth
                    sx={{ justifyContent: 'space-between', textAlign: 'left' }}
                  >
                    <span>{u.name}</span>
                    <Typography component="span" variant="caption" color="text.secondary">
                      {u.email} · {u.role}
                    </Typography>
                  </Button>
                ))}
              </Stack>
            </>
          )}
        </Stack>
      </Paper>
    </Box>
  );
}
